import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { type Location, useListLocations } from '../../../app/ApiGen';
import { CacheAwareRefreshButton } from '../../../components/DataTable/CacheRefreshButton';
import { ColumnFiltersToggle } from '../../../components/DataTable/Toolbar';
import { DataErrorHandler } from '../../../components/ErrorHandler';
import { ExportCsv, type ExportProps } from '../../../components/ExportCsv';
import {
  ColumnChanger,
  type ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Workspace,
} from '../../../design-system';
import { navigateOnRowClick } from '../../../utils/dataTable/rowHelpers';
import { organisationHasAttributes } from '../../../utils/format';
import { tableColumns } from '../../../utils/locations/table';
import { normalise, type Normalised } from '../../../utils/request';
import { dateFormat } from '../../../utils/sessions/columns/format';
import { csvDateFormat } from '../../Organisations/Organisation/Reports/utils';
import { LocationMap } from './LocationMap';
import type { LocationData } from './types';

const toRowData = (locations: Normalised<Location>[]): LocationData[] =>
  locations.map((location) => {
    const { id, name, address, chargePoints, organisation, created, updated, coordinates, icpNumber, icpDetails } =
      location;
    const { country } = address;
    const orgHasAttributes = organisationHasAttributes(organisation);
    const organisationLabel = orgHasAttributes ? organisation.name : organisation?.id ?? '';
    const organisationSlug = orgHasAttributes ? organisation.slug : '';
    return {
      id,
      name,
      address,
      country,
      chargePointCount: chargePoints.length,
      organisationName: organisationLabel,
      organisationSlug,
      createdDate: created,
      updatedDate: updated,
      coordinates,
      icpNumber,
      gridExitPoint: icpDetails?.networkConnectionPoint,
      electricityDistributor: icpDetails?.electricityDistributor,
      electricityRetailer: icpDetails?.electricityRetailer,
    };
  });

export const LocationsList: React.FC = () => {
  const [filtering, setFiltering] = React.useState(false);

  const history = useHistory();
  const { loading, error, data, refetch } = useListLocations({});

  const handleRefresh = () => {
    refetch({ queryParams: { forceRefresh: true } }).catch(() => {
      console.log('Error refreshing locations');
    });
  };

  const cacheInfo = data?.cache as { lastUpdated?: string } | undefined;

  if (error) {
    return <DataErrorHandler refetch={refetch} description="Unable to load locations" error={error} />;
  }

  const locations = data ? toRowData(normalise(data)) : [];
  const getLocationUrl = (rowData: LocationData) =>
    `/organisations/${rowData.organisationSlug}/locations/${rowData.id}`;

  const columns = tableColumns<LocationData>({ getUrl: getLocationUrl });

  return (
    <Workspace maxWidth="xl">
      <Card>
        {locations && <LocationMap locations={locations} />}
        <DataTable<LocationData>
          options={{ columnsButton: true, filtering }}
          toolbarProps={{
            search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
              <SearchField
                dataManager={dataManager}
                item="locations"
                onSearchChanged={onSearchChanged}
                search={search}
              />
            ),
            actions: (props: React.PropsWithChildren<ColumnChangerProps> & ExportProps<LocationData>) => (
              <>
                <ColumnChanger
                  columnsButton={props.columnsButton}
                  columns={props.columns}
                  icons={props.icons}
                  onColumnsChanged={props.onColumnsChanged}
                />
                <ColumnFiltersToggle filtering={filtering} setFiltering={setFiltering} />
                <CacheAwareRefreshButton onRefresh={handleRefresh} isLoading={loading} cacheInfo={cacheInfo} />
                <ExportCsv<LocationData>
                  columns={props.columns}
                  data={props.data}
                  dateFormat={csvDateFormat}
                  exportFileName="all-locations"
                  getFieldValue={props.getFieldValue}
                  icons={props.icons}
                />
                <Button color="primary" component={Link} to="/admin/migrate/locations" variant="contained">
                  Move locations
                </Button>
              </>
            ),
          }}
          columns={[
            columns.name,
            columns.address,
            { field: 'country', title: 'Country', hidden: true, width: '5%' },
            columns.icpNumber,
            columns.gridExitPoint,
            columns.electricityDistributor,
            columns.electricityRetailer,
            columns.chargePointCount,
            { field: 'organisationName', title: 'Organisation', width: '10%' },
            {
              field: 'createdDate',
              title: 'Created date',
              type: 'date',
              hidden: true,
              render: ({ createdDate }) => dateFormat(createdDate),
              width: '15%',
            },
            {
              field: 'updatedDate',
              title: 'Updated date',
              type: 'date',
              hidden: true,
              render: ({ updatedDate }) => dateFormat(updatedDate),
              width: '15%',
            },
          ]}
          data={locations}
          onRowClick={navigateOnRowClick((rowData) => getLocationUrl(rowData), history)}
        />
      </Card>
    </Workspace>
  );
};
